import type { ReactElement, SyntheticEvent } from "react"
import type { ButtonAtmProps } from "@onestore/hel/dist/components/atoms/ButtonAtm"
import ButtonAtm from "@onestore/hel/dist/components/atoms/ButtonAtm"
import FlexContainerOrg from "@onestore/hel/dist/components/organisms/FlexContainerOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import ConditionalWrapper from "@onestore/hel/dist/helpers/ConditionalWrapper"
import _t from "@core/i18n"
import useBasketActions from "@gatsby-plugin-basket/hooks/useBasketActions"
import type { CloudBluePlan } from "@gatsby-plugin-definitions/fragments/CloudBluePlan"
import Tick from "@onestore-icons/tick.svg"
import ProductPrice from "../ProductPrice"

export interface BasketButtonProps extends ButtonAtmProps {
  plan: CloudBluePlan[]
  hasSurface?: boolean
  hidePrice?: boolean
}

function BasketButton({
  onClick,
  href,
  title,
  text,
  color,
  variant,
  size,
  hidePrice,
  hasSurface,
  plan,
  rel,
  ...restProps
}: BasketButtonProps): ReactElement<BasketButtonProps> {
  const { addProductToBasket, isLoading, isDone, goToBasket } =
    useBasketActions(plan[0])

  const handleOnClick = (event: SyntheticEvent) => {
    event.preventDefault()

    if (onClick) {
      onClick(event)
    }

    addProductToBasket()
  }

  const priceElement = <ProductPrice hasTextColor plan={plan} />

  return (
    <ConditionalWrapper
      condition={!hidePrice}
      wrapper={(children) => (
        <FlexContainerOrg
          alignItems="center"
          display="flex"
          justifyContent="space-between"
        >
          <PushOrg
            rightSpace={{ small: 1, medium: 1, large: 2 }}
            leftSpace={{ large: 0, medium: 0, small: 1 }}
            hasNoReset
          >
            {priceElement}
          </PushOrg>

          {children}
        </FlexContainerOrg>
      )}
    >
      <ButtonAtm
        onClick={isDone ? goToBasket : handleOnClick}
        isLoading={isLoading}
        title={isDone ? _t("button.genericPageSuccess") : title}
        color={color}
        icon={isDone ? Tick : undefined}
        variant={variant}
        size={size}
        rel={rel}
        {...restProps}
      >
        {isDone ? _t("preBasket.upsell.added") : text}
      </ButtonAtm>
    </ConditionalWrapper>
  )
}

export default BasketButton
